import * as THREE from 'three'
import { TweenLite, Power1, Power2, TimelineLite } from 'gsap'

import Terrain from '../Terrain.js'
import ScanModel from '../ScanModel.js'
import BarrelsInteraction from '../Interactions/Barrels.js'

export default class Protect
{
    constructor(_options)
    {
        this.$eventHub = _options.$eventHub
        this.camera = _options.camera
        this.loader = _options.loader
        this.time = _options.time
        this.sizes = _options.sizes
        this.ui = _options.ui
        this.mobile = _options.mobile
        this.cursor = _options.cursor
        this.position = _options.position
        this.terrainGeometry = _options.terrainGeometry

        this.name = 'protect'
        this.container = new THREE.Object3D()
        this.container.position.copy(this.position)

        this.setTerrain()
    }

    /**
     * Show
     */
    show()
    {
        this.introTimeline.seek(this.introTimeline.duration())
    }

    /**
     * Hide
     */
    hide()
    {
    }

    /**
     * Set terrain
     */
    setTerrain()
    {
        this.terrain = new Terrain({
            mobile: this.mobile,
            time: this.time,
            loader: this.loader,
            ui: this.ui,
            geometry: this.terrainGeometry,
            texture: this.loader.resources.terrainTexture
        })

        this.terrain.plane.material.uniforms.uElevationNoiseMultiplier.value = 0.07

        this.container.add(this.terrain.container)
        window.terrain = this.terrain
    }

    /**
     * Set contents
     */
    setContents()
    {
        this.setNanopix()
        this.setFactory()
        this.setInteraction()
        this.setIntroTimeline()
        this.setFocusTimeline()
    }

    /**
     * Set nanopix
     */
    setNanopix()
    {
        this.nanopix = {}

        // Container
        this.nanopix.container = new THREE.Object3D()
        this.nanopix.container.rotation.y = - 2.3
        this.nanopix.container.scale.x = 0.2
        this.nanopix.container.scale.y = 0.2
        this.nanopix.container.scale.z = 0.2
        this.nanopix.container.position.y = 0
        this.nanopix.container.position.z = -5.3
        this.nanopix.container.position.x = 0.4
        this.container.add(this.nanopix.container)

        // Rover A
        this.nanopix.roverA = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.roverModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 0.18, y: 0.18, z: 0.18 }
        })
        this.nanopix.container.add(this.nanopix.roverA.container)

        // Camera B
        this.nanopix.cameraB = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.nanopixModelObj.geometry,
            revealPosition: { x: 0, y: 0.92, z: - 0.19 },
            color: { x: 1, y: 0.9, z: 0 }
        })
        this.nanopix.container.add(this.nanopix.cameraB.container)
    }

    /**
     * Set factory
     */
    setFactory()
    {
        this.factory = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.factoryModelObj.geometry,
            revealPosition: { x: -37.35, y: 0, z: 3.45 },
            color: { x: 0, y: 0, z: 0 }
        })
        this.factory.container.rotation.y = Math.PI * 0.5
        this.factory.container.scale.x = 0.2
        this.factory.container.scale.y = 0.2
        this.factory.container.scale.z = 0.2
        this.factory.container.position.y = 0
        this.factory.container.position.z = - 4
        this.factory.container.position.x = 0
        this.container.add(this.factory.container)
    }

    /**
     * Set interaction
     */
    setInteraction()
    {
        this.interaction = new BarrelsInteraction({
            $eventHub: this.$eventHub,
            mobile: this.mobile,
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            cursor: this.cursor,
            ui: this.ui,
            loader: this.loader
        })

        this.interaction.container.position.x = 0.14
        this.interaction.container.position.z = - 5.9
        this.interaction.container.position.y = - 3.34

        this.interaction.container.rotation.order = 'YXZ'
        this.interaction.container.rotation.x = 0.64
        this.interaction.container.rotation.y = 0.52
        this.interaction.container.rotation.z = 0
    }

    setIntroTimeline()
    {
        this.introTimeline = new TimelineLite({ paused: true })
        this.introTimeline.timeScale(1)

        // Terrain
        this.introTimeline.call(() => { this.terrain.reset() }, null, this, 0)

        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uElevationMultiplier, 2, { value: 1 }), 3)
        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uDistortionProgress, 4, { value: 0 }), 1)
        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uFloatingAmplitude, 4, { value: 0, ease: Power2.easeInOut }), 0)
        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uEdgeScale, 4, { value: 1 }), 3)
        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uColor.value, 3, { x: 0.12, y: 0.12, z: 0.12 }), 5)
        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uFocusDistance.value, 3, { y: 0.4 }), 2)
        this.introTimeline.add(TweenLite.to(this.terrain.plane.material.uniforms.uFocusMultiplier, 3, { value: 0 }), 3)

        // Nanopix
        this.introTimeline.add(TweenLite.to(this.nanopix.roverA.material.uniforms.uColor.value, 2, { x: 0.28, y: 0.28, z: 0.28 }), 1.5)
        this.introTimeline.add(TweenLite.fromTo(this.nanopix.roverA.material.uniforms.uRevealDistance, 2, { value: 1.24 }, { value: 0, ease: Power2.easeInOut }), 4)
        this.introTimeline.add(TweenLite.fromTo(this.nanopix.cameraB.material.uniforms.uRevealDistance, 2, { value: 1.25 }, { value: 0, ease: Power2.easeInOut }), 4.2)

        // Factory
        this.introTimeline.add(TweenLite.fromTo(this.factory.material.uniforms.uRevealDistance, 4, { value: 37 }, { value: 0, ease: Power1.easeOut }), 4.6)
    }

    /**
     * Set focus timeline
     */
    setFocusTimeline()
    {
        this.focusTimeline = new TimelineLite({ paused: true })

        this.focusTimeline.call(() =>
        {
            // Entering
            if(!this.focusTimeline._reversed)
            {
                this.container.add(this.interaction.container)
            }
            // Leaving
            else
            {
                this.container.remove(this.interaction.container)
            }
        }, null, this, 0)

        // Terrain
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusColor.value, 2, { x: 1, y: 0.9, z: 0 }, { x: 0.2, y: 0.2, z: 0.12 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusDistance.value, 2, { y: 0.4 }, { y: 0.4 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusPosition.value, 2, { x: 0.1, y: 0, z: - 5.2 }, { x: 0.345, y: 0, z: - 5.168 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uColor.value, 2, { x: 0.12, y: 0.12, z: 0.12 }, { x: 0.06, y: 0.06, z: 0.06 }), 0)

        // Factory
        this.focusTimeline.add(TweenLite.fromTo(this.factory.material.uniforms.uColor.value, 2, { x: 0.1, y: 0.1, z: 0.1 }, { x: 0.04, y: 0.04, z: 0.04 }), 0)

        // Nanopix
        this.focusTimeline.add(TweenLite.fromTo(this.nanopix.roverA.material.uniforms.uColor.value, 2, { x: 0.28, y: 0.28, z: 0.28 }, { x: 0.25, y: 0.25, z: 0.25 }), 0)

        // Barrels
        this.focusTimeline.call(() =>
        {
            if(!this.focusTimeline._reversed)
            {
                this.interaction.reset()
            }
        }, null, this, 0)
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Scenes/Protect.js